import { IconClock, IconTruck } from "@tine/designsystem-icons/sharp";
import { Group } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import isNil from "lodash-es/isNil";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { logger } from "../../common/logger";
import { ORDERTYPE } from "../../common/types/productOrderTypes";
import { translateDayLabel } from "../../common/utils";
import { isLoading } from "../../common/utils/asyncDataUtils";
import { formatDeliveryDate } from "../../common/utils/dateUtils";
import Button from "../../components/Button";
import MainColumn from "../../components/common/MainColumn";
import DeliveryDateButton from "../../components/deliverydates/DeliveryDateButton";
import Loading from "../../components/Loading";
import authStore from "../../stores/auth/authStore";
import cartStore from "../../stores/cart/cartStore";
import deliveryDatesStore from "../../stores/deliveryDates/deliveryDatesStore";
import theme from "../../themes/theme";
import ChangeOrderOrSubscription from "../ChangeOrderOrSubscription";
import Deadline from "./Deadline";

const iconAndLabelClassName = cn(["flex items-center", "font-bold"]);

const DeliveryInfo = () => {
   if (!authStore.isLoggedIn() || authStore.error) {
      return null;
   }

   if (isLoading(deliveryDatesStore.deliveryDates)) {
      return <Loading label="Laster leveringsdager..." />;
   }

   if (cartStore.isEditing() && !isNil(cartStore.editing)) {
      return <ChangeOrderOrSubscription />;
   }

   const dayLabel = translateDayLabel(cartStore.orderType, theme.deliveryMethod);
   const shouldShowDeadline = cartStore.orderType === ORDERTYPE.HPN || theme.deliveryMethod === "inhouse";
   const currentDelivery = deliveryDatesStore.getCurrentDelivery();
   if (currentDelivery === null) {
      logger.log("delivery", "No current delivery found");
      return null;
   }

   const renderDeliveryDateButton: (dates: Date[], isPickerOpen: boolean, setPickerOpen: (show: boolean) => void) => ReactNode = (
      _dates,
      isPickerOpen,
      setPickerOpen
   ) => {
      return (
         <Button variant="tertiary" onClick={() => setPickerOpen(!isPickerOpen)}>
            Endre
         </Button>
      );
   };

   return (
      <MainColumn className="shadow-md bg-surface-default">
         <Group gap={8} className="py-2">
            {shouldShowDeadline && (
               <Group gap={2}>
                  <div className={iconAndLabelClassName}>
                     <IconClock className="mr-2" />
                     <p className="p-0 m-0">Frist:</p>
                  </div>
                  {!!currentDelivery.deadline && <Deadline />}
               </Group>
            )}
            <Group gap={2}>
               <div className={iconAndLabelClassName}>
                  <IconTruck className="mr-2" />
                  <p className="p-0 m-0">{dayLabel}:</p>
               </div>
               <Group gap={0}>
                  <span>{formatDeliveryDate(currentDelivery.date, cartStore.orderType, currentDelivery.interval)}</span>
                  {shouldShowDeadline && (
                     <>
                        <span className="ms-3">|</span>
                        <DeliveryDateButton render={renderDeliveryDateButton} />
                     </>
                  )}
               </Group>
            </Group>
         </Group>
      </MainColumn>
   );
};

export default observer(DeliveryInfo);
